import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../../firebase/config';
import Modal from '../Modal';
import TextField from '../TextField';
import Button from '../Button';
import Icon from '../Icon';
import { FiSearch } from 'react-icons/fi';
import { resetUserPassword } from '../../services/passwordService';
import { findBankByAuthUid } from '../../screens/login/service/auth';
import { fetchPSOs, type PSO } from '../../screens/pso/service/psoService';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export default function ChangeUserPasswordModal({ isOpen, onClose }: Props) {
    const [bankId, setBankId] = useState<string | null>(null);
    const [users, setUsers] = useState<PSO[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [userSearch, setUserSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedUser, setSelectedUser] = useState<PSO | null>(null);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const isBankUser = sessionStorage.getItem('role') === 'bank';

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        if (showDropdown) document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [showDropdown]);

    useEffect(() => {
        if (!isOpen) return;

        // reset transient UI each open
        setError(null);
        setSuccess(false);
        setNewPassword('');
        setConfirmPassword('');
        setUserSearch('');
        setShowDropdown(false);
        setSelectedUser(null);

        const uid = auth.currentUser?.uid;
        if (!uid || !isBankUser) return;

        const load = async () => {
            setLoadingUsers(true);
            try {
                const bank = await findBankByAuthUid(uid);
                if (!bank?.bankId) throw new Error('Bank not found for current user');
                setBankId(bank.bankId);

                const list = await fetchPSOs(bank.bankId);
                setUsers(list);
            } catch (e: any) {
                setError(e?.message || 'Failed to load users');
            } finally {
                setLoadingUsers(false);
            }
        };

        load();
    }, [isOpen, isBankUser]);

    const resettableUsers = useMemo(() => {
        // Only users that actually have a Firebase Auth account can have their password reset.
        // In this codebase, that corresponds to PSOs created with `createUserWithEmailAndPassword` (incharge),
        // which store the Firebase Auth uid in `authUid`.
        return users.filter((u) => Boolean(u.authUid));
    }, [users]);

    const filteredUsers = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        const base = resettableUsers;
        if (!term) return base;
        return base.filter((u) => {
            const name = (u.name || '').toString().toLowerCase();
            const email = (u.email || '').toString().toLowerCase();
            const designation = (u.designation || '').toString().replace(/_/g, ' ').toLowerCase();
            return name.includes(term) || email.includes(term) || designation.includes(term);
        });
    }, [userSearch, resettableUsers]);

    const targetUid = selectedUser?.authUid || '';

    const handleClose = () => {
        setShowDropdown(false);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!isBankUser) {
            setError('This option is available for Bank users only');
            return;
        }

        if (!selectedUser || !targetUid) {
            setError('Please select a user');
            return;
        }

        if (!selectedUser.authUid) {
            setError('Selected user does not have a login account (no authUid). Only incharge users can be reset.');
            return;
        }

        if (!newPassword || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setSubmitting(true);
        try {
            await resetUserPassword(targetUid, newPassword);
            setSuccess(true);
            setTimeout(() => handleClose(), 1500);
        } catch (e: any) {
            setError(e?.message || 'Failed to reset user password');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Change User Password">
            <form onSubmit={handleSubmit} className="space-y-4 p-6">
                {success && (
                    <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-200">
                        Password updated successfully!
                    </div>
                )}

                {error && (
                    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
                        {error}
                    </div>
                )}

                {!isBankUser ? (
                    <div className="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                        This option is available for Bank users only.
                    </div>
                ) : (
                    <>
                        <div className="relative" ref={dropdownRef}>
                            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                Select User
                            </label>
                            <div className="relative">
                                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    <Icon icon={FiSearch} className="h-4 w-4" />
                                </div>
                                <input
                                    value={userSearch}
                                    onChange={(e) => {
                                        setUserSearch(e.target.value);
                                        setShowDropdown(true);
                                        setSelectedUser(null);
                                    }}
                                    onFocus={() => setShowDropdown(true)}
                                    placeholder={loadingUsers ? 'Loading users...' : 'Search incharge users by name / email / designation'}
                                    disabled={loadingUsers || submitting || success}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-10 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                />
                            </div>

                            {showDropdown && (
                                <div className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                    {filteredUsers.length === 0 ? (
                                        <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                            No resettable users found.
                                        </div>
                                    ) : (
                                        filteredUsers.map((u) => {
                                            const designation = (u.designation || '').toString().replace(/_/g, ' ');
                                            return (
                                                <button
                                                    type="button"
                                                    key={u.id}
                                                    onClick={() => {
                                                        setSelectedUser(u);
                                                        setUserSearch(`${u.name || 'User'}${u.email ? ` (${u.email})` : ''}`);
                                                        setShowDropdown(false);
                                                    }}
                                                    className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                                                >
                                                    <div className="font-medium text-gray-900 dark:text-gray-100">{u.name || '—'}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {u.email || '—'} {designation ? `• ${designation}` : ''}
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}

                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {bankId ? `Bank: ${bankId}` : ''}
                            </div>
                        </div>

                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            disabled={submitting || success || !selectedUser}
                            required
                        />

                        <TextField
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            disabled={submitting || success || !selectedUser}
                            required
                        />
                    </>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={submitting || success || !isBankUser || !selectedUser?.authUid}
                    >
                        {submitting ? 'Updating...' : 'Update Password'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

